import React, { useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Pause, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

const ACTIVE_BOOKING_HINT = /active[_ ]?bookings?/i;

export default function VehicleHostActions({ vehicle, onUpdated }) {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const status = vehicle?.status;
  const canManage = status === "approved" || status === "paused";
  if (!canManage) return null;

  const patchStatus = async (nextStatus) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/vehicles/${vehicle.vehicle_id}/status`,
        { status: nextStatus },
        { withCredentials: true },
      );
      toast.success(
        nextStatus === "paused" ? "Listing paused"
          : nextStatus === "approved" ? "Listing resumed"
          : "Removed from platform",
      );
      setConfirm(null);
      onUpdated?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not update listing";
      if (ACTIVE_BOOKING_HINT.test(msg) || detail === "active_bookings") {
        setBlocked(true);
        toast.error("Cannot change status while this vehicle has active bookings");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`veh-host-actions-${vehicle.vehicle_id}`}>
        {status === "approved" && (
          <button
            type="button"
            onClick={() => setConfirm("paused")}
            disabled={busy || blocked}
            className="px-3 h-8 text-xs zc-btn-ghost inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`pause-listing-${vehicle.vehicle_id}`}
          >
            <Pause size={12} /> Pause listing
          </button>
        )}
        {status === "paused" && (
          <button
            type="button"
            onClick={() => setConfirm("approved")}
            disabled={busy || blocked}
            className="px-3 h-8 text-xs zc-btn-primary inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`resume-listing-${vehicle.vehicle_id}`}
          >
            Resume listing
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirm("offboarded")}
          disabled={busy || blocked}
          className="px-3 h-8 text-xs inline-flex items-center gap-1 rounded-md border disabled:opacity-40"
          style={{ color: "var(--zc-danger,#dc2626)", borderColor: "rgba(220,38,38,0.35)" }}
          data-testid={`offboard-listing-${vehicle.vehicle_id}`}
        >
          <Trash2 size={12} /> Remove from platform
        </button>
      </div>
      {blocked && (
        <div className="mt-2 text-2xs text-[var(--zc-warn,#f59e0b)]">
          Status changes blocked while active bookings exist.
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="bg-white border border-[var(--zc-border)] max-w-md" data-testid="veh-status-confirm">
          <DialogHeader>
            <DialogTitle className="zc-headline text-xl">
              {confirm === "paused" ? "Pause listing?" : confirm === "approved" ? "Resume listing?" : "Remove from platform?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--zc-text-2)]">
            {confirm === "paused"
              ? "Your vehicle will be hidden from search until you resume. Existing bookings are unaffected."
              : confirm === "approved"
              ? "Your vehicle will appear in search again for new bookings. Existing bookings are unaffected."
              : "This permanently removes the vehicle from the marketplace. You cannot undo this from the app."}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setConfirm(null)} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
            <button
              type="button"
              onClick={() => patchStatus(confirm)}
              disabled={busy}
              className="px-4 h-10 text-sm rounded-md text-white disabled:opacity-50"
              style={{ background: confirm === "offboarded" ? "var(--zc-danger,#dc2626)" : "var(--zc-accent)" }}
              data-testid="veh-status-confirm-submit"
            >
              {busy ? "Updating…" : confirm === "paused" ? "Pause listing" : confirm === "approved" ? "Resume listing" : "Remove"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function vehicleStatusBadgeClass(status) {
  if (status === "approved") return "text-[var(--zc-success)]";
  if (status === "rejected" || status === "offboarded") return "text-[var(--zc-danger)]";
  if (status === "paused") return "text-[var(--zc-warn,#f59e0b)]";
  return "text-[var(--zc-warn)]";
}
